import React, { useState, useEffect } from 'react';
import { UserPlus, Trash2, Shield, User, Key, X, Edit2 } from 'lucide-react';
import { addLog } from '../../utils/operationLog';
import { supabase } from '../../supabase/client';

interface AdminUser {
  id: string;
  username: string;
  password: string;
  role: 'super' | 'normal';
  name: string;
  created_at: string;
}

export default function AdminUsers() {
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ username: '', password: '', name: '' });
  const [error, setError] = useState('');
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showResetAdminModal, setShowResetAdminModal] = useState(false);
  const [resetAdminId, setResetAdminId] = useState<string | null>(null);
  const [resetAdminPassword, setResetAdminPassword] = useState('');
  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [newMasterName, setNewMasterName] = useState('');
  const [showEditUsernameModal, setShowEditUsernameModal] = useState(false);
  const [newMasterUsername, setNewMasterUsername] = useState('');
  const [showEditPasswordModal, setShowEditPasswordModal] = useState(false);
  const [newMasterPassword, setNewMasterPassword] = useState('');
  const [confirmMasterPassword, setConfirmMasterPassword] = useState('');
  const [currentAdmin, setCurrentAdmin] = useState<AdminUser | null>(null);

  useEffect(() => {
    loadAdmins();
    loadCurrentAdmin();
  }, []);

  const loadCurrentAdmin = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data } = await supabase
        .from('admin_users')
        .select('*')
        .eq('id', user.id)
        .single();
      if (data) setCurrentAdmin(data);
    }
  };

  const loadAdmins = async () => {
    const { data } = await supabase.from('admin_users').select('*').order('created_at', { ascending: false });
    if (data) setAdmins(data);
  };

  const handleAddAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!newAdmin.username || !newAdmin.password || !newAdmin.name) {
      setError('请填写所有字段');
      return;
    }
    const { data: existing } = await supabase.from('admin_users').select('id').eq('username', newAdmin.username).single();
    if (existing) {
      setError('用户名已存在');
      return;
    }
    const { data, error: insertError } = await supabase.from('admin_users').insert({
      username: newAdmin.username,
      password: newAdmin.password,
      role: 'normal',
      name: newAdmin.name
    }).select().single();
    if (insertError) {
      setError('添加失败');
      return;
    }
    if (data && currentAdmin) {
      addLog(currentAdmin.id, currentAdmin.name, currentAdmin.role, '添加普通管理员', data.name, data.username);
    }
    setNewAdmin({ username: '', password: '', name: '' });
    setShowAddModal(false);
    loadAdmins();
  };

  const handleDeleteAdmin = async (id: string) => {
    if (!confirm('确定要删除这个普通管理员吗？')) return;
    const adminToDelete = admins.find(a => a.id === id);
    const { error } = await supabase.from('admin_users').delete().eq('id', id);
    if (!error && adminToDelete && currentAdmin) {
      addLog(currentAdmin.id, currentAdmin.name, currentAdmin.role, '删除普通管理员', adminToDelete.name, adminToDelete.username);
    }
    loadAdmins();
  };

  const openResetAdminModal = (id: string) => {
    setResetAdminId(id);
    setResetAdminPassword('');
    setShowResetAdminModal(true);
  };

  const handleResetAdminPassword = async () => {
    if (!resetAdminId || resetAdminPassword.length < 6) return;
    const passwordRegex = /^[a-zA-Z0-9_+-]+$/;
    if (!passwordRegex.test(resetAdminPassword)) {
      alert('密码只能包含大写字母、小写字母、数字、下划线、+、-');
      return;
    }
    const adminToReset = admins.find(a => a.id === resetAdminId);
    const { error } = await supabase.from('admin_users').update({ password: resetAdminPassword }).eq('id', resetAdminId);
    if (!error && adminToReset && currentAdmin) {
      addLog(currentAdmin.id, currentAdmin.name, currentAdmin.role, '重置普通管理员密码', adminToReset.name, adminToReset.username);
    }
    setShowResetAdminModal(false);
    setResetAdminId(null);
    setResetAdminPassword('');
    alert('密码重置成功');
  };

  const masterAdmin = (admins || []).find(a => a.role === 'super');
  const subAdmins = (admins || []).filter(a => a.role === 'normal');

  const handleEditMasterName = () => {
    if (masterAdmin) {
      setNewMasterName(masterAdmin.name);
      setShowEditNameModal(true);
    }
  };

  const handleSaveMasterName = async () => {
    if (!newMasterName.trim()) {
      alert('姓名不能为空');
      return;
    }
    if (masterAdmin) {
      await supabase.from('admin_users').update({ name: newMasterName.trim() }).eq('id', masterAdmin.id);
      setShowEditNameModal(false);
      loadAdmins();
      alert('姓名修改成功');
    }
  };

  const handleEditMasterUsername = () => {
    if (masterAdmin) {
      setNewMasterUsername(masterAdmin.username);
      setShowEditUsernameModal(true);
    }
  };

  const handleSaveMasterUsername = async () => {
    if (!newMasterUsername.trim()) {
      alert('用户名不能为空');
      return;
    }
    if (!masterAdmin) {
      alert('超级管理员数据不存在');
      return;
    }
    const { data: existing, error: checkError } = await supabase.from('admin_users').select('id').eq('username', newMasterUsername.trim()).maybeSingle();
    if (checkError) {
      
      alert('检查用户名失败: ' + checkError.message);
      return;
    }
    if (existing && existing.id !== masterAdmin.id) {
      alert('用户名已存在');
      return;
    }
    const { error: updateError } = await supabase.from('admin_users').update({ username: newMasterUsername.trim() }).eq('id', masterAdmin.id);
    if (updateError) {
      
      alert('更新失败: ' + updateError.message);
      return;
    }
    setShowEditUsernameModal(false);
    await loadAdmins();
    alert('用户名修改成功');
  };

  const handleEditMasterPassword = () => {
    setNewMasterPassword('');
    setConfirmMasterPassword('');
    setShowEditPasswordModal(true);
  };

  const handleSaveMasterPassword = async () => {
    if (!newMasterPassword.trim()) {
      alert('密码不能为空');
      return;
    }
    if (newMasterPassword.length < 6) {
      alert('密码至少6位');
      return;
    }
    const passwordRegex = /^[a-zA-Z0-9_+-]+$/;
    if (!passwordRegex.test(newMasterPassword)) {
      alert('密码只能包含大写字母、小写字母、数字、下划线、+、-');
      return;
    }
    if (newMasterPassword !== confirmMasterPassword) {
      alert('两次输入的密码不一致');
      return;
    }
    if (masterAdmin) {
      await supabase.from('admin_users').update({ password: newMasterPassword }).eq('id', masterAdmin.id);
      setShowEditPasswordModal(false);
      setNewMasterPassword('');
      setConfirmMasterPassword('');
      loadAdmins();
      alert('密码修改成功');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">管理员管理</h2>
        <button onClick={() => setShowAddModal(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          <UserPlus size={18} />添加普通管理员
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-amber-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800">超级管理员</h3>
          </div>
          {masterAdmin ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="w-12 h-12 bg-amber-200 rounded-full flex items-center justify-center">
                  <Shield className="w-6 h-6 text-amber-700" />
                </div>
                  <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-800">超级管理员</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-gray-500">@{masterAdmin.username}</p>
                    <button onClick={handleEditMasterUsername} className="p-1 text-gray-400 hover:text-blue-600 rounded transition-colors" title="编辑用户名">
                      <Edit2 className="w-3 h-3" />
                    </button>
                  </div>
                  <button onClick={handleEditMasterPassword} className="mt-1 text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1">
                    <Key className="w-3 h-3" />修改密码
                  </button>
                </div>
                <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">超级管理员</span>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <Shield className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>暂无超级管理员数据</p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Key className="w-5 h-5 text-orange-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800">账号安全</h3>
          </div>
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="w-12 h-12 bg-orange-200 rounded-full flex items-center justify-center">
                <Key className="w-6 h-6 text-orange-700" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-800">修改密码</p>
                <p className="text-sm text-gray-500">定期更换密码保障账号安全</p>
              </div>
              <button onClick={() => setShowChangePasswordModal(true)} className="flex items-center gap-1 px-3 py-1.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 text-sm">
                <Key size={14} />修改
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <User className="w-5 h-5 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800">普通管理员</h3>
          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">{subAdmins.length}人</span>
        </div>
        {subAdmins.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <User className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>暂无普通管理员</p>
          </div>
        ) : (
          <div className="space-y-3">
            {subAdmins.map(admin => (
              <div key={admin.id} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border">
                <div className="w-10 h-10 bg-blue-200 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-blue-700" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-800">{admin.name}</p>
                  <p className="text-sm text-gray-500">@{admin.username}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => openResetAdminModal(admin.id)} className="flex items-center gap-1 px-3 py-1.5 bg-orange-100 text-orange-600 rounded-lg hover:bg-orange-200 text-sm">
                    <Key size={14} />重置密码
                  </button>
                  <button onClick={() => handleDeleteAdmin(admin.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold text-gray-800 mb-4">添加普通管理员</h3>
            {error && <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">{error}</div>}
            <form onSubmit={handleAddAdmin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">显示名称</label>
                <input type="text" value={newAdmin.name} onChange={e => setNewAdmin({ ...newAdmin, name: e.target.value })} className="w-full px-4 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">用户名</label>
                <input type="text" value={newAdmin.username} onChange={e => setNewAdmin({ ...newAdmin, username: e.target.value })} className="w-full px-4 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">密码</label>
                <input type="text" value={newAdmin.password} onChange={e => setNewAdmin({ ...newAdmin, password: e.target.value })} className="w-full px-4 py-2 border rounded-lg" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowAddModal(false)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg">取消</button>
                <button type="submit" className="flex-1 py-2 bg-blue-600 text-white rounded-lg">添加</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showChangePasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold text-gray-800 mb-4">修改密码</h3>
            {passwordError && <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">{passwordError}</div>}
            <form onSubmit={async (e) => {
              e.preventDefault();
              setPasswordError('');
              if (!currentAdmin) {
                setPasswordError('请先登录');
                return;
              }
              const { data: adminData } = await supabase.from('admin_users').select('password').eq('id', currentAdmin.id).single();
              if (!adminData || adminData.password !== oldPassword) {
                setPasswordError('原密码错误');
                return;
              }
              if (newAdminPassword !== confirmPassword) {
                setPasswordError('两次输入的新密码不一致');
                return;
              }
              const passwordRegex = /^[a-zA-Z0-9_+-]+$/;
              if (!passwordRegex.test(newAdminPassword)) {
                setPasswordError('密码只能包含大写字母、小写字母、数字、下划线、+、-');
                return;
              }
              if (newAdminPassword.length < 6) {
                setPasswordError('密码至少6位');
                return;
              }
              await supabase.from('admin_users').update({ password: newAdminPassword }).eq('id', currentAdmin.id);
              addLog(currentAdmin.id, currentAdmin.name, currentAdmin.role, '修改密码', '自己', currentAdmin.username);
              setShowChangePasswordModal(false);
              setOldPassword('');
              setNewAdminPassword('');
              setConfirmPassword('');
              alert('密码修改成功');
            }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">原密码</label>
                <input type="password" value={oldPassword} onChange={e => setOldPassword(e.target.value)} className="w-full px-4 py-2 border rounded-lg" placeholder="请输入原密码" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">新密码</label>
                <input type="password" value={newAdminPassword} onChange={e => setNewAdminPassword(e.target.value)} className="w-full px-4 py-2 border rounded-lg" placeholder="6位以上" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">确认新密码</label>
                <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full px-4 py-2 border rounded-lg" placeholder="请再次输入新密码" required />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowChangePasswordModal(false); setPasswordError(''); setOldPassword(''); setNewAdminPassword(''); setConfirmPassword(''); }} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg">取消</button>
                <button type="submit" className="flex-1 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600">确认修改</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showResetAdminModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold text-gray-800 mb-4">重置普通管理员密码</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">新密码</label>
                <input type="password" value={resetAdminPassword} onChange={e => setResetAdminPassword(e.target.value)} className="w-full px-4 py-2 border rounded-lg" placeholder="6位以上" />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowResetAdminModal(false)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg">取消</button>
                <button onClick={handleResetAdminPassword} disabled={resetAdminPassword.length < 6} className="flex-1 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:bg-gray-400">确认重置</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditNameModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold text-gray-800 mb-4">修改超级管理员姓名</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">姓名</label>
                <input type="text" value={newMasterName} onChange={e => setNewMasterName(e.target.value)} className="w-full px-4 py-2 border rounded-lg" placeholder="请输入姓名" />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowEditNameModal(false)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg">取消</button>
                <button onClick={handleSaveMasterName} disabled={!newMasterName.trim()} className="flex-1 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400">保存</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditUsernameModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold text-gray-800 mb-4">修改超级管理员用户名</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">用户名</label>
                <input type="text" value={newMasterUsername} onChange={e => setNewMasterUsername(e.target.value)} className="w-full px-4 py-2 border rounded-lg" placeholder="请输入用户名" />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowEditUsernameModal(false)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg">取消</button>
                <button onClick={handleSaveMasterUsername} disabled={!newMasterUsername.trim()} className="flex-1 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400">保存</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold text-gray-800 mb-4">修改超级管理员密码</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">新密码</label>
                <input type="password" value={newMasterPassword} onChange={e => setNewMasterPassword(e.target.value)} className="w-full px-4 py-2 border rounded-lg" placeholder="6位以上，支持字母、数字、_、+、-" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">确认新密码</label>
                <input type="password" value={confirmMasterPassword} onChange={e => setConfirmMasterPassword(e.target.value)} className="w-full px-4 py-2 border rounded-lg" placeholder="请再次输入新密码" />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowEditPasswordModal(false)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg">取消</button>
                <button onClick={handleSaveMasterPassword} disabled={!newMasterPassword.trim() || newMasterPassword.length < 6} className="flex-1 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400">保存</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
